/**
 * OTC Request API Endpoint definitions.
 * Centralized registry for all OTC-related administration endpoints.
 */
export const OTC_ENDPOINTS = {
  /**
   * Escalate an OTC request to a compliance role or team admin.
   * Method: POST
   */
  ESCALATE: (otcId: string) => `/v1/admin/otc/${otcId}/escalate`,

  /**
   * Approve an OTC request or send a new offer / counter-offer.
   * Method: POST
   */
  OFFER: (otcId: string) => `/v1/admin/otc/${otcId}/offer`,

  /**
   * Update high-level OTC request status (e.g., Reject or Cancel).
   * Method: PATCH / PUT
   */
  STATUS: (otcId: string) => `/v1/admin/otc/${otcId}/status`,

  /**
   * Update settlement transaction status (Processing, Success with receipt URL, or Failed with reason).
   * Method: PATCH / PUT
   */
  TRANSACTION_STATUS: (otcId: string) => `/v1/admin/otc/${otcId}/transaction/status`,

  /**
   * Execute settlement payout (Domestic NGN or External international USD).
   * Method: POST
   */
  SETTLE: (otcId: string) => `/v1/admin/otc/${otcId}/settle`,

  /**
   * Fetch saved external accounts / payout providers matching currency and optional search.
   * Method: GET
   */
  EXTERNAL_ACCOUNTS: (currency: string = "NGN", search: string = 'bank') => {
    const params = new URLSearchParams({ currency });
    if (search && search.trim()) {
      params.append("search", search.trim());
    }
    return `/v1/admin/otc/external-accounts?${params.toString()}`;
  },

  /**
   * Fetch available settlement providers filtered by currency conversion pair.
   * Method: GET
   */
  PROVIDERS: (currencyFrom = "USD", currencyTo = "NGN") =>
    `/v1/admin/otc/providers?currencyFrom=${encodeURIComponent(currencyFrom)}&currencyTo=${encodeURIComponent(currencyTo)}`,

  /**
   * Add / create a new OTC settlement provider.
   * Method: POST
   */
  CREATE_PROVIDER: "/v1/admin/otc/providers",

  /**
   * Fetch bank list filtered by currency conversion pair and optional source.
   * Method: GET
   */
  BANKS: (currencyFrom = "NGN", currencyTo = "NGN", source?: string) => {
    const params = new URLSearchParams({
      currencyFrom,
      currencyTo,
    });
    if (
      source &&
      typeof source === "string" &&
      source.trim() &&
      source.trim().toLowerCase() !== "null" &&
      source.trim().toLowerCase() !== "undefined"
    ) {
      params.append("source", source.trim());
    }
    return `/v1/admin/otc/banks?${params.toString()}`;
  },

  /**
   * Verify bank account details and retrieve name enquiry reference.
   * Supports optional source query parameter.
   * Method: POST
   */
  VERIFY_BANK_DETAIL: (source?: string) => {
    if (
      source &&
      typeof source === "string" &&
      source.trim() &&
      source.trim().toLowerCase() !== "null" &&
      source.trim().toLowerCase() !== "undefined"
    ) {
      const params = new URLSearchParams({ source: source.trim() });
      return `/v1/admin/otc/verify-bank-detail?${params.toString()}`;
    }
    return "/v1/admin/otc/verify-bank-detail";
  },
  // DOWNLOAD_TRX_RECEIPT: (transactionId: string) => `/v1/admin/transaction/${transactionId}/receipt`;
} as const;

